import React, { useEffect, useState, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import LocalizedHeader from "../components/landing/LocalizedHeader";
import LocalizedHeroSection from "../components/landing/LocalizedHeroSection";
import { MarqueeSection, TrustBar } from "../components/landing/MarqueeSection";
import api from "../lib/api";
import { getTranslation, SUPPORTED_LANGUAGES } from "../locales/translations";
import { getFaqs } from "../locales/faqs";

// Lazy load below-the-fold components for better FCP
const HowItWorks = lazy(() => import("../components/landing/HowItWorks"));
const QuizCTA = lazy(() => import("../components/landing/QuizCTA"));
const QuizModal = lazy(() => import("../components/landing/QuizModal"));
const VIPClubBenefits = lazy(() => import("../components/landing/VIPClubBenefits"));
const TutorialsSection = lazy(() => import("../components/landing/TutorialsSection"));
const SpokesSection = lazy(() => import("../components/landing/SpokesSection"));
const LocalizedBlogSection = lazy(() => import("../components/landing/LocalizedBlogSection"));
const LocalizedFAQSection = lazy(() => import("../components/landing/LocalizedFAQSection"));
const LocalizedNewsletterSection = lazy(() => import("../components/landing/LocalizedNewsletterSection"));
const LocalizedFooter = lazy(() => import("../components/landing/LocalizedFooter"));
const LiveActivityFeed = lazy(() => import("../components/landing/LiveActivityFeed"));
const AnimatedCounters = lazy(() => import("../components/landing/AnimatedCounters"));
const ExitIntentPopup = lazy(() => import("../components/landing/ExitIntentPopup"));

// URL path to language code mapping (SEO-Optimized Structure)
const PATH_TO_LANG = {
  "/": "en",        // Root redirects to English (x-default)
  "/en": "en",      // English Global
  "/de": "de",      // German (Germany)
  "/nl": "nl",      // Dutch (Netherlands)
  "/en-ca": "en-ca", // English Canadian
  "/da": "da",      // Danish (Denmark)
};

const faqSchemaMarkup = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

const orgSchemaMarkup = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://cryptovipclub.ai/#organization",
  name: "CryptoVIP Club",
  alternateName: "CryptoVIP",
  url: "https://cryptovipclub.ai",
  logo: {
    "@type": "ImageObject",
    url: "https://cryptovipclub.ai/logo.png",
    width: 512,
    height: 512
  },
  description: "The world's first VIP casino negotiators. We bridge the gap between high-value crypto gamblers and premium casinos, securing exclusive VIP deals, higher cashback rates, and personalized bonuses.",
  foundingDate: "2024",
  slogan: "Your VIP Status Awaits",
  areaServed: ["Worldwide"],
  knowsLanguage: ["English", "German", "Dutch", "Danish"],
  sameAs: [
    "https://twitter.com/cryptovipclub",
    "https://t.me/cryptovipclub"
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "contact@cryptovipclub.ai",
    availableLanguage: ["English", "German", "Dutch", "Danish"]
  },
  address: {
    "@type": "PostalAddress",
    addressCountry: "MT"
  },
  brand: {
    "@type": "Brand",
    name: "CryptoVIP Club",
    slogan: "The World's First VIP Casino Negotiators"
  },
  memberOf: {
    "@type": "Organization",
    name: "Responsible Gambling Council"
  }
};

const websiteSchemaMarkup = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "CryptoVIP Club",
  url: "https://cryptovipclub.ai",
  potentialAction: {
    "@type": "SearchAction",
    target: "https://cryptovipclub.ai/search?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

export default function LocalizedLandingPage() {
  const location = useLocation();
  const [casinos, setCasinos] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isQuizOpen, setIsQuizOpen] = useState(false);

  // Get language code from current path
  const currentPath = location.pathname;
  const langCode = PATH_TO_LANG[currentPath] || "en";
  const t = getTranslation(langCode);
  const faqs = getFaqs(langCode);
  
  // Get current language info for canonical URL
  const langInfo = SUPPORTED_LANGUAGES.find(l => l.code === langCode) || SUPPORTED_LANGUAGES[0];
  const canonicalUrl = `https://cryptovipclub.ai${langInfo.path}`;

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Seed data first
        await api.seedData();
        // Load localized data and latest articles in parallel
        const [landingResponse, articlesResponse] = await Promise.all([
          api.getLandingData(langCode),
          api.getLatestArticles(6),
        ]);
        setCasinos(landingResponse.data.casinos);
        setArticles(articlesResponse.data);
      } catch (err) {
        console.error("Failed to load data:", err);
        // Fallback to regular casinos endpoint
        try {
          const casinosRes = await api.getCasinos();
          setCasinos(casinosRes.data);
        } catch (e) {
          console.error("Fallback also failed:", e);
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [langCode]);

  return (
    <>
      <Helmet>
        <html lang={langCode.split('-')[0]} />
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <meta name="keywords" content={t.meta.keywords} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        {/* hreflang tags for SEO (Google 2025-2026 Guidelines) */}
        <link rel="alternate" hrefLang="en" href="https://cryptovipclub.ai/en" />
        <link rel="alternate" hrefLang="de" href="https://cryptovipclub.ai/de" />
        <link rel="alternate" hrefLang="nl" href="https://cryptovipclub.ai/nl" />
        <link rel="alternate" hrefLang="en-ca" href="https://cryptovipclub.ai/en-ca" />
        <link rel="alternate" hrefLang="da" href="https://cryptovipclub.ai/da" />
        <link rel="alternate" hrefLang="x-default" href="https://cryptovipclub.ai/en" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t.meta.title} />
        <meta property="og:description" content={t.meta.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="CryptoVIP Club" />
        <meta property="og:locale" content={langCode.replace("-", "_")} />

        {/* Open Graph Image */}
        <meta property="og:image" content="https://cryptovipclub.ai/og-image.png" />
        <meta property="og:image:width" content="1536" />
        <meta property="og:image:height" content="1024" />
        <meta property="og:image:alt" content="CryptoVIP Club - Best Crypto Casinos & VIP Bonuses" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@cryptovipclub" />
        <meta name="twitter:title" content={t.meta.title} />
        <meta name="twitter:description" content={t.meta.description} />
        <meta name="twitter:image" content="https://cryptovipclub.ai/og-image.png" />

        {/* Schema Markup */}
        <script type="application/ld+json">{JSON.stringify(orgSchemaMarkup)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchemaMarkup)}</script>
        {faqs.length > 0 && (
          <script type="application/ld+json">{JSON.stringify(faqSchemaMarkup(faqs))}</script>
        )}
      </Helmet>

      <LocalizedHeader t={t} currentLang={langCode} />
      <main>
        <LocalizedHeroSection t={t} />
        <MarqueeSection />
        <TrustBar t={t} />
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <HowItWorks t={t} />
          <QuizCTA t={t} onOpenQuiz={() => setIsQuizOpen(true)} />
          <VIPClubBenefits t={t} />
          <AnimatedCounters t={t} />
          <TutorialsSection t={t} currentLang={langCode} />
          <SpokesSection t={t} currentLang={langCode} />
          {!loading && articles.length > 0 && <LocalizedBlogSection articles={articles} t={t} />}
          <LocalizedFAQSection faqs={faqs} t={t} />
          <LocalizedNewsletterSection t={t} />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <LocalizedFooter t={t} currentLang={langCode} />
        <LiveActivityFeed />
        <QuizModal isOpen={isQuizOpen} onClose={() => setIsQuizOpen(false)} />
        <ExitIntentPopup onStartQuiz={() => setIsQuizOpen(true)} />
      </Suspense>
    </>
  );
}
